import React, { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash, MagicWand } from "@phosphor-icons/react";
import { api, money } from "@/lib/api";
import ClientCombobox from "@/components/ClientCombobox";
import { toast } from "sonner";

const emptyItem = () => ({ service_id: null, description: "", quantity: 1, unit_price: 0 });

export default function DocumentDialog({ open, onClose, type, initial, presetClientId, presetDate, onSaved }) {
  // type: "quote" | "job" | "invoice"
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [crew, setCrew] = useState([]);
  const [clientId, setClientId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [title, setTitle] = useState("");
  const [items, setItems] = useState([emptyItem()]);
  const [taxRate, setTaxRate] = useState(0);
  const [discountType, setDiscountType] = useState("none");
  const [discountValue, setDiscountValue] = useState(0);
  const [depositPct, setDepositPct] = useState(0);
  const [showTax, setShowTax] = useState(false);
  const [notes, setNotes] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [assignedCrew, setAssignedCrew] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.listClients().then(setClients).catch(() => {});
    api.listServices().then(setServices).catch(() => {});
    if (type === "job") api.listUsers().then(setCrew).catch(() => {});
  }, [open, type]);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setClientId(initial.client_id || "");
      setPropertyId(initial.property_id || "");
      setTitle(initial.title || "");
      setItems(initial.line_items?.length ? initial.line_items.map((i) => ({ ...i })) : [emptyItem()]);
      setTaxRate(initial.tax_rate || 0);
      setShowTax((initial.tax_rate || 0) > 0);
      setDiscountType(initial.discount_type || "none");
      setDiscountValue(initial.discount_value || 0);
      setDepositPct(initial.deposit_pct || 0);
      setNotes(initial.notes || "");
      setDueDate(initial.due_date || "");
      setScheduledDate(initial.scheduled_date || "");
      setScheduledTime(initial.scheduled_time || "");
      setAssignedCrew(initial.assigned_crew_ids || []);
    } else {
      setClientId(presetClientId || ""); setPropertyId(""); setTitle(""); setItems([emptyItem()]);
      setTaxRate(0); setShowTax(false); setDiscountType("none"); setDiscountValue(0); setDepositPct(0);
      setNotes(""); setDueDate(""); setScheduledDate(presetDate || "");
      setScheduledTime(""); setAssignedCrew([]);
    }
  }, [open, initial]);

  const selectedClient = clients.find((c) => c.id === clientId);
  const properties = selectedClient?.properties || [];

  const subtotal = useMemo(
    () => items.reduce((s, i) => s + Number(i.quantity || 0) * Number(i.unit_price || 0), 0),
    [items]
  );
  const tax = subtotal * (Number(taxRate) / 100);
  const totalBeforeDiscount = subtotal + tax;
  const discountAmount =
    discountType === "percent" ? totalBeforeDiscount * (Number(discountValue || 0) / 100)
    : discountType === "flat" ? Math.min(Number(discountValue || 0), totalBeforeDiscount)
    : 0;
  const total = Math.max(totalBeforeDiscount - discountAmount, 0);
  const depositAmount = total * (Number(depositPct || 0) / 100);

  const updateItem = (idx, patch) =>
    setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)));

  const pickService = (idx, serviceId) => {
    const svc = services.find((s) => s.id === serviceId);
    if (svc) updateItem(idx, { service_id: svc.id, description: svc.name, unit_price: svc.unit_price });
  };

  const save = async () => {
    if (!clientId) return toast.error("Please select a client");
    const cleanItems = items
      .filter((i) => i.description.trim())
      .map((i) => ({
        service_id: i.service_id || null,
        description: i.description,
        quantity: Number(i.quantity) || 0,
        unit_price: Number(i.unit_price) || 0,
      }));
    if (!cleanItems.length) return toast.error("Add at least one line item");
    if (type === "job" && !title.trim()) return toast.error("Job title is required");

    const base = {
      client_id: clientId,
      property_id: propertyId || null,
      line_items: cleanItems,
      tax_rate: Number(taxRate) || 0,
      discount_type: discountType,
      discount_value: Number(discountValue) || 0,
      deposit_pct: Number(depositPct) || 0,
      notes,
    };
    let payload = base;
    if (type === "job")
      payload = { ...base, title, scheduled_date: scheduledDate || null, scheduled_time: scheduledTime || null, assigned_crew_ids: assignedCrew };
    if (type === "invoice") payload = { ...base, due_date: dueDate || null };

    setSaving(true);
    try {
      const fn = {
        quote: initial ? () => api.updateQuote(initial.id, payload) : () => api.createQuote(payload),
        job: initial ? () => api.updateJob(initial.id, payload) : () => api.createJob(payload),
        invoice: initial ? () => api.updateInvoice(initial.id, payload) : () => api.createInvoice(payload),
      }[type];
      const res = await fn();
      toast.success(`${type[0].toUpperCase() + type.slice(1)} ${initial ? "updated" : "created"}`);
      onSaved?.(res);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const toggleCrew = (id) =>
    setAssignedCrew((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="document-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl capitalize">
            {initial ? "Edit" : "New"} {type}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5">
          {type === "job" && (
            <div>
              <Label>Job Title</Label>
              <Input data-testid="job-title-input" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. House & driveway wash" className="mt-1.5" />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Client</Label>
              <ClientCombobox clients={clients} value={clientId} onChange={(v) => { setClientId(v); setPropertyId(""); }} />
            </div>
            <div>
              <Label>Property</Label>
              <Select value={propertyId} onValueChange={setPropertyId} disabled={!properties.length}>
                <SelectTrigger data-testid="property-select" className="mt-1.5"><SelectValue placeholder={properties.length ? "Select property" : "No properties"} /></SelectTrigger>
                <SelectContent>
                  {properties.map((p) => (<SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {type === "job" && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Scheduled Date</Label>
                <Input data-testid="job-date-input" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} className="mt-1.5" />
              </div>
              <div>
                <Label>Time</Label>
                <Input data-testid="job-time-input" type="time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} className="mt-1.5" />
              </div>
              <div className="col-span-2">
                <Label>Assign Crew</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {crew.filter((u) => u.role === "crew").length === 0 && (
                    <span className="text-xs text-slate-400">No crew members yet — add them in Team.</span>
                  )}
                  {crew.filter((u) => u.role === "crew").map((u) => (
                    <button
                      key={u.id}
                      type="button"
                      onClick={() => toggleCrew(u.id)}
                      data-testid={`crew-toggle-${u.id}`}
                      className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors duration-200 ${
                        assignedCrew.includes(u.id)
                          ? "bg-[#0B1726] text-white border-[#0B1726]"
                          : "bg-white text-slate-600 border-slate-300 hover:border-slate-400"
                      }`}
                    >
                      {u.name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {type === "invoice" && (
            <div className="w-1/2">
              <Label>Due Date</Label>
              <Input data-testid="invoice-due-input" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="mt-1.5" />
            </div>
          )}

          {/* Line items */}
          <div>
            <Label className="mb-2 block">Line Items</Label>
            <div className="border border-slate-200 rounded-md overflow-hidden">
              <div className="grid grid-cols-12 gap-2 bg-slate-50 px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
                <div className="col-span-6">Description</div>
                <div className="col-span-2 text-center">Qty</div>
                <div className="col-span-2 text-right">Rate</div>
                <div className="col-span-2 text-right">Amount</div>
              </div>
              {items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 px-3 py-2 border-t border-slate-100 items-center">
                  <div className="col-span-6 flex gap-1.5">
                    {services.length > 0 && (
                      <Select value={it.service_id || ""} onValueChange={(v) => pickService(idx, v)}>
                        <SelectTrigger className="w-10 px-0 justify-center shrink-0" title="Pick from catalog"><MagicWand size={15} /></SelectTrigger>
                        <SelectContent>
                          {services.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name} — {money(s.unit_price)}</SelectItem>))}
                        </SelectContent>
                      </Select>
                    )}
                    <Input data-testid={`item-desc-${idx}`} value={it.description} onChange={(e) => updateItem(idx, { description: e.target.value, service_id: null })} placeholder="Service description" />
                  </div>
                  <Input data-testid={`item-qty-${idx}`} type="number" className="col-span-2 text-center" value={it.quantity} onChange={(e) => updateItem(idx, { quantity: e.target.value })} />
                  <Input data-testid={`item-rate-${idx}`} type="number" className="col-span-2 text-right" value={it.unit_price} onChange={(e) => updateItem(idx, { unit_price: e.target.value })} />
                  <div className="col-span-2 flex items-center justify-end gap-1">
                    <span className="text-sm font-semibold">{money(Number(it.quantity || 0) * Number(it.unit_price || 0))}</span>
                    <button onClick={() => setItems((p) => p.filter((_, i) => i !== idx))} className="text-slate-400 hover:text-rose-500 transition-colors duration-200" data-testid={`item-remove-${idx}`}>
                      <Trash size={15} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
            <Button variant="ghost" size="sm" onClick={() => setItems((p) => [...p, emptyItem()])} className="mt-2 text-[#00B4D8] hover:text-cyan-600" data-testid="add-item-button">
              <Plus size={16} className="mr-1" /> Add line item
            </Button>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Notes</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Internal notes or terms" className="mt-1.5 h-24" data-testid="notes-input" />
            </div>
            <div className="bg-slate-50 rounded-md p-4 self-end">
              <div className="flex justify-between text-sm py-1"><span className="text-slate-500">Subtotal</span><span className="font-semibold">{money(subtotal)}</span></div>

              {showTax ? (
                <div className="flex justify-between text-sm py-1 items-center">
                  <span className="text-slate-500">Tax</span>
                  <div className="flex items-center gap-1">
                    <Input type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="w-14 h-7 text-right text-xs" data-testid="tax-rate-input" />
                    <span className="text-slate-400 text-xs">%</span>
                    <span className="font-semibold w-16 text-right">{money(tax)}</span>
                  </div>
                </div>
              ) : (
                <button type="button" onClick={() => setShowTax(true)} className="text-xs text-slate-400 hover:text-[#00B4D8] py-1 transition-colors duration-200" data-testid="add-tax-toggle">+ Add tax</button>
              )}

              <div className="flex justify-between text-sm py-1 items-center">
                <span className="text-slate-500">Discount</span>
                <div className="flex items-center gap-1">
                  <Select value={discountType} onValueChange={setDiscountType}>
                    <SelectTrigger className="h-7 w-[86px] text-xs px-2" data-testid="discount-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">None</SelectItem>
                      <SelectItem value="percent">% Off</SelectItem>
                      <SelectItem value="flat">$ Off</SelectItem>
                    </SelectContent>
                  </Select>
                  {discountType !== "none" && <Input type="number" value={discountValue} onChange={(e) => setDiscountValue(e.target.value)} className="w-14 h-7 text-right text-xs" data-testid="discount-value" />}
                  <span className="font-semibold w-16 text-right text-rose-600">{discountAmount > 0 ? `-${money(discountAmount)}` : money(0)}</span>
                </div>
              </div>

              <div className="flex justify-between pt-2 mt-1 border-t border-slate-300"><span className="font-bold">Total</span><span className="font-display font-extrabold text-lg text-[#00B4D8]">{money(total)}</span></div>

              <div className="flex justify-between text-sm py-1 items-center pt-2 mt-1 border-t border-slate-200">
                <span className="text-slate-500">Deposit</span>
                <div className="flex items-center gap-1">
                  <Input type="number" value={depositPct} onChange={(e) => setDepositPct(e.target.value)} className="w-14 h-7 text-right text-xs" data-testid="deposit-pct" />
                  <span className="text-slate-400 text-xs">%</span>
                  <span className="font-semibold w-16 text-right">{money(depositAmount)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="dialog-cancel">Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B1726] hover:bg-slate-800" data-testid="dialog-save">
            {saving ? "Saving..." : `Save ${type}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
